<template>
    <v-container fluid grid-list-md>
        <v-form ref="form">
            <!-- category card -->
            <v-card flat v-if="hasProgram('Peak-Shaving')">
                <v-card-text>
                    <!-- CONFIG ITEMS -->
                    <!-- NCP Overrides -->
                    <ConfigItem >
                        <template slot="title">
                            NCP Peak Day Override
                        </template>
                        <template slot="description">
                            If MANUAL mode, you are in control. In AUTO, you are
                            allowing Sustaine&trade; to evaluate the marketplace and
                            determine peak mode automatically.
                        </template>
                        <template slot="form">
                            <v-radio-group v-model="peakMode" row>
                                <template v-slot:label>
                                    <strong>Mode:</strong>
                                </template>
                                <v-radio value="manual" color="primary">
                                    <template v-slot:label>
                                        <strong class="primary--text">Manual</strong>
                                    </template>
                                </v-radio>
                                <v-radio value="auto" disabled>
                                    <template v-slot:label>
                                        <strong>Auto</strong>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                            <!-- Manual Mode (on or off) -->
                            <v-switch
                                v-model="peakModeManual"
                                v-if="peakMode == 'manual'"
                                :key="`peakmode-switch-${componentKey}`"
                                color="primary"
                                >
                                <template v-slot:label>
                                    <strong> Manual Mode is <span class="primary--text">{{ manualModeLabel }}</span></strong>
                                </template>
                            </v-switch>
                        </template>
                    </ConfigItem>
                    <!-- NCP Ceiling -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            NCP Ceiling
                        </template>
                        <template slot="description">
                            Targeted maximum demand which we plan to stay under at all times.
                        </template>
                        <template slot="form">
                            <!-- Manual Mode (on or off) -->
                            <v-switch
                                v-model="ncpCeilingSwitch"
                                @change="setNcpCeiling"
                                :key="`ceiling-switch-${componentKey}`"
                                color="primary"
                                >
                                <template v-slot:label>
                                    <strong>
                                    NCP Ceiling is
                                    <span class="primary--text">
                                    {{ ncpCeilingLabel }}</span
                                        ></strong
                                        >
                                </template>
                            </v-switch>
                            <v-text-field
                                v-if="ncpCeilingSwitch"
                                v-model="ncpCeiling"
                                :rules="ceilingRules"
                                :key="`ceiling-switch-text${componentKey}`"
                                label="NCP Ceiling"
                                >
                                <template v-slot:append-outer>
                                    <small>kW</small>
                                </template>
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- 5CP Utility Forecast Adjustment Factor -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            5CP Utility Forecast Adjustment Factor
                        </template>
                        <template slot="description">
                            Multiplier on utility’s peak load forecast.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="adjustFactor1cp"
                                :rules="factorRules"
                                label="Adjustment Factor"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- 5CP Utility Previous Minimum Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            5CP Utility Previous Minimum Threshold
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum utility peak.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="previousMinThres1cp"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- ISO Forecast Adjustment Factor -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            ISO Forecast Adjustment Factor
                        </template>
                        <template slot="description">
                            Multiplier on ISO's peak load forecast.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="adjustFactor5cp"
                                :rules="factorRules"
                                label="Adjustment Factor"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- ISO Previous Minimum Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            ISO Previous Minimum Threshold
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum ISO peak.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="previousMinThres5cp"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- June: Previous Min Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            On-Peak Previous Minimum Threshold, June
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum utility peak for June.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="onPeakThresholdJun"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- July: Previous Min Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            On-Peak Previous Minimum Threshold, July
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum utility peak for July.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="onPeakThresholdJul"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- Aug: Previous Min Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            On-Peak Previous Minimum Threshold, August
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum utility peak for August.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="onPeakThresholdAug"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>
                    <!-- Sep: Previous Min Threshold -->
                    <ConfigItem :divider="true">
                        <template slot="title">
                            On-Peak Previous Minimum Threshold, September
                        </template>
                        <template slot="description">
                            Multiplier on last year’s minimum utility peak for September.
                        </template>
                        <template slot="form">
                            <v-text-field
                                v-model="onPeakThresholdSep"
                                :rules="factorRules"
                                label="Threshold"
                                >
                            </v-text-field>
                        </template>
                    </ConfigItem>

                    <ConfigItem :divider="true" v-if="env != 'production'">
                        <template slot="title">
                            Development Testing: Current Time 
                        </template>
                        <template slot="description">
                            To be used for testing only.  Environment = {{ env }}.
                        </template>
                        <template slot="form">
                            <h4 class="body-2 font-weight-bold pt-5">
                                <date-picker
                                    v-model="devTimeNow"
                                    :minute-step="15"
                                    clearable
                                    confirm
                                    width="275"
                                    lang="en"
                                    type="datetime"
                                    format="YYYY-MM-DD HH:mm:ss">
                                </date-picker>
                            </h4>

                        </template>
                    </ConfigItem>

                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="onSave()"
                        color="primary"
                        pr-4
                        pb-3>
                        Update</v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-container>
</template>
<script>
import ConfigService from '@/services/ConfigService'
import { mapGetters, mapMutations } from 'vuex'
import moment from 'moment'
import ConfigItem from './ConfigItem'
export default {
  name: 'SiteConfig',
  components: {
    ConfigItem
  },
  data() {
    return {
      env: process.env.VUE_APP_ENV,
      devTimeNow: '',
      componentKey: 0,
      loaded: false,
      valid: false,
      factorRules: [
        v =>
          (v > 0 && v < 2) || 'Required.  Must be greater than 0, less than 2'
      ],
      ceilingRules: [
        v =>
          (v >= 0 && v < 100000) || 'Required.  Must be between 0 and 100,000'
      ],
      saveErr: false,
      peakMode: '',
      peakModeManual: '',
      ncpCeiling: '',
      ncpCeilingSwitch: '',
      keys: null,

      adjustFactor1cp: '',
      previousMinThres1cp: '',
      adjustFactor5cp: '',
      previousMinThres5cp: '',
      onPeakAdjustFactor: '', //'1.3245',
      onPeakThresholdJun: '', //'0.9881',
      onPeakThresholdJul: '', //'1.9999',
      onPeakThresholdAug: '', //'0.0010',
      onPeakThresholdSep: '', //'1.0000'

    }
  },
  computed: {
    ...mapGetters(['getFacilityId', 'getFacilityTimezone', 'hasProgram']),
    manualModeLabel() {
      if (this.peakModeManual) return 'ON'
      return 'OFF'
    },
    ncpCeilingLabel() {
      if (this.ncpCeilingSwitch) return 'SET'
      return 'NOT SET'
    }
  },
  created() {
    this.createKeyMap()
    this.getPeakConfig()
  },
  methods: {
    ...mapMutations(['SET_NCP_CEILING']),

    /**
     * NCP Ceiling switch
     */
    setNcpCeiling() {
      // if going from off to on, set 0 value to default
      if (this.ncpCeilingSwitch) {
        if (this.ncpCeiling === 0) {
          this.ncpCeiling = 4000
          this.$notify.info("Enabling NCP Ceiling, set to default 4000 kW")
        }
      }
      this.componentKey += 1
    },

    /**
     * Generate a map tying local data to config var names
     */
    createKeyMap() {
      this.keys = new Map([
        ['MODE', 'PEAK_DAY_FACILITY_' + this.getFacilityId + '_MODE'],
        ['MODE_TOGGLE', 'PEAK_DAY_FACILITY_' + this.getFacilityId + '_MANUAL'],
        ['CEILING', 'NCP_CEILING_FACILITY_' + this.getFacilityId],
        ['1CP_FUDGE', 'PEAK_DAY_5CP_UTIL_FUDGE_FACILITY_' + this.getFacilityId],
        ['1CP_THRESH', 'PEAK_DAY_5CP_UTIL_PERCENT_FACILITY_' + this.getFacilityId],
        ['5CP_FUDGE', 'PEAK_DAY_5CP_ISO_FUDGE_FACILITY_' + this.getFacilityId],
        ['5CP_THRESH', 'PEAK_DAY_5CP_ISO_PERCENT_FACILITY_' + this.getFacilityId],
        ['1CP_THRESH_JUN', 'PEAK_DAY_1CP_PERCENT_FACILITY_' + this.getFacilityId + '_MONTH_6'],
        ['1CP_THRESH_JUL', 'PEAK_DAY_1CP_PERCENT_FACILITY_' + this.getFacilityId + '_MONTH_7'],
        ['1CP_THRESH_AUG', 'PEAK_DAY_1CP_PERCENT_FACILITY_' + this.getFacilityId + '_MONTH_8'],
        ['1CP_THRESH_SEP', 'PEAK_DAY_1CP_PERCENT_FACILITY_' + this.getFacilityId + '_MONTH_9']
      ])
    },
    
    /**
     * Load all config vars; save defaults if not found
     */
    async getPeakConfig() {
      try {
        const modePromise = this.getConfig(this.keys.get('MODE'))
        const manualPromise = this.getConfig(this.keys.get('MODE_TOGGLE'))
        const ceilingPromise = this.getConfig(this.keys.get('CEILING'))
        const adjustPromise1cp = this.getConfig(this.keys.get('1CP_FUDGE'))
        const thresPromise1cp = this.getConfig(this.keys.get('1CP_THRESH'))
        const thresPromise1cpJun = this.getConfig(this.keys.get('1CP_THRESH_JUN'))
        const thresPromise1cpJul = this.getConfig(this.keys.get('1CP_THRESH_JUL'))
        const thresPromise1cpAug = this.getConfig(this.keys.get('1CP_THRESH_AUG'))
        const thresPromise1cpSep = this.getConfig(this.keys.get('1CP_THRESH_SEP'))
        const adjustPromise5cp = this.getConfig(this.keys.get('5CP_FUDGE'))
        const thresPromise5cp = this.getConfig(this.keys.get('5CP_THRESH'))
        const savedNow = this.getConfig('NOW')

        const [
          mode,
          manual,
          ceiling,
          adjust1cp,
          thresh1cp,
          thresh1cpJun,
          thresh1cpJul,
          thresh1cpAug,
          thresh1cpSep,
          adjust5cp,
          thresh5cp,
          nowDateTime
        ] = await Promise.all([
          modePromise,
          manualPromise,
          ceilingPromise,
          adjustPromise1cp,
          thresPromise1cp,
          thresPromise1cpJun,
          thresPromise1cpJul,
          thresPromise1cpAug,
          thresPromise1cpSep,
          adjustPromise5cp,
          thresPromise5cp,
          savedNow
        ])

        // **
        let defaultFactor = parseFloat(1.0).toFixed(4)
        if (this.hasProgram('Peak-Shaving')) {
            if (mode == null) {
                this.postConfig(this.keys.get('MODE'), 'manual')
                    this.peakMode = 'manual'
                } else {
                    this.peakMode = mode
            }

            if (manual == null) {
                this.postConfig(this.keys.get('MODE_TOGGLE'), false)
                this.peakModeManual = false
            } else {
                this.peakModeManual = manual === 'true' ? true : false
            }

            if (ceiling == null) {
                this.postConfig(this.keys.get('CEILING'), 0)
                this.ncpCeiling = 0
                this.ncpCeilingSwitch = false
            } else {
                let ncpc = parseInt(ceiling)
                this.ncpCeiling = ncpc
                if (ncpc === 0) {
                    this.ncpCeilingSwitch = false
                } else {
                    this.ncpCeilingSwitch = true
                }
            }

            if (adjust1cp == null) {
                this.postConfig(this.keys.get('1CP_FUDGE'), defaultFactor)
                this.adjustFactor1cp = defaultFactor
            } else {
                this.adjustFactor1cp = parseFloat(adjust1cp).toFixed(4)
            }

            if (thresh1cp == null) {
                this.postConfig(this.keys.get('1CP_THRESH'), defaultFactor)
                this.previousMinThres1cp = defaultFactor
            } else {
                this.previousMinThres1cp = parseFloat(thresh1cp).toFixed(4)
            }

            if (thresh1cpJun == null) {
                this.postConfig(this.keys.get('1CP_THRESH_JUN'), defaultFactor)
                this.onPeakThresholdJun = defaultFactor
            } else {
                this.onPeakThresholdJun = parseFloat(thresh1cpJun).toFixed(4)
            }

            if (thresh1cpJul == null) {
                this.postConfig(this.keys.get('1CP_THRESH_JUL'), defaultFactor)
                this.onPeakThresholdJul = defaultFactor
            } else {
                this.onPeakThresholdJul = parseFloat(thresh1cpJul).toFixed(4)
            }

            if (thresh1cpAug == null) {
                this.postConfig(this.keys.get('1CP_THRESH_AUG'), defaultFactor)
                this.onPeakThresholdAug = defaultFactor
            } else {
                this.onPeakThresholdAug = parseFloat(thresh1cpAug).toFixed(4)
            }

            if (thresh1cpSep == null) {
                this.postConfig(this.keys.get('1CP_THRESH_SEP'), defaultFactor)
                this.onPeakThresholdSep = defaultFactor
            } else {
                this.onPeakThresholdSep = parseFloat(thresh1cpSep).toFixed(4)
            }

            if (adjust5cp == null) {
                this.postConfig(this.keys.get('5CP_FUDGE'), defaultFactor)
                this.adjustFactor5cp = defaultFactor
            } else {
                this.adjustFactor5cp = parseFloat(adjust5cp).toFixed(4)
            }

            if (thresh5cp == null) {
                this.postConfig(this.keys.get('5CP_THRESH'), defaultFactor)
                this.previousMinThres5cp = defaultFactor
            } else {
                this.previousMinThres5cp = parseFloat(thresh5cp).toFixed(4)
            }

            if (nowDateTime != null) {
                this.devTimeNow = nowDateTime;
            }

        }

        // console.log('peakMode', this.peakMode)
        // console.log('peakModeManual', this.peakModeManual)
        // console.log('ncpCeiling', this.ncpCeiling)
        // console.log('adjustFactor1cp', this.adjustFactor1cp)
        // console.log('previousMinThres1cp', this.previousMinThres1cp)
        // console.log('adjustFactor5cp', this.adjustFactor5cp)
        // console.log('previousMinThres5cp', this.previousMinThres5cp)

        this.loaded = true
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * Save all config vars
     */
    onSave() {
      // console.log('peakMode', this.peakMode)
      // console.log('peakModeManual', this.peakModeManual)
      // console.log('ncpCeiling (0-100,000)', this.ncpCeiling)
      // console.log('adjustFactor1cp (0.1-1.99)', this.adjustFactor1cp)
      // console.log('previousMinThres1cp (0.1-1.99)', this.previousMinThres1cp)
      // console.log('adjustFactor5cp  (0.1-1.99)', this.adjustFactor5cp)
      // console.log('previousMinThres5cp (0.1-1.99)', this.previousMinThres5cp)

        if (this.hasProgram('Peak-Shaving')) {
            this.postConfig(this.keys.get('MODE'), this.peakMode)
            this.postConfig(this.keys.get('MODE_TOGGLE'), this.peakModeManual)

            // check switch on before setting/saving
            if (!this.ncpCeilingSwitch) {
                this.ncpCeiling = 0
            }
            this.postConfig(this.keys.get('CEILING'), parseFloat(this.ncpCeiling))
            this.SET_NCP_CEILING({ ceiling: this.ncpCeiling })

            this.postConfig(this.keys.get('1CP_FUDGE'), this.adjustFactor1cp)
            this.postConfig(this.keys.get('1CP_THRESH'), this.previousMinThres1cp)
            this.postConfig(this.keys.get('1CP_THRESH_JUN'), this.onPeakThresholdJun)
            this.postConfig(this.keys.get('1CP_THRESH_JUL'), this.onPeakThresholdJul)
            this.postConfig(this.keys.get('1CP_THRESH_AUG'), this.onPeakThresholdAug)
            this.postConfig(this.keys.get('1CP_THRESH_SEP'), this.onPeakThresholdSep)

            this.postConfig(this.keys.get('5CP_FUDGE'), this.adjustFactor5cp)
            this.postConfig(this.keys.get('5CP_THRESH'), this.previousMinThres5cp)

            if(this.devTimeNow) {
                // convert local to UTC time, then format to ISO
                let formatted = moment(moment(this.devTimeNow).utc()).toISOString()
                this.postConfig('NOW', formatted);
                // console.log("devTimeNow", this.devTimeNow);
                // console.log("formatted", formatted);
            }
        }

        this.$toast.success("Configuration successfully saved.")
        this.componentKey += 1

    },

    /**
     * Fetch config var value
     */
    async getConfig(key) {
      try {
        let response = await ConfigService.getValue(key)
        if (response.data) {
          return response.data.value
        }
      } catch (error) {}
      return null
    },

    /**
     * Post config var value
     */
    postConfig(key, value) {
      ConfigService.postValue(key, value)
        // .then(response => {})
        .catch(error => { this.$toast.error("Cannot post updates for key " + key + " " + error.message) })
    }
  }
}
</script>